import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';

import styles from "../../detail.module.css";
import LineGraph from "../line_graph/line_graph";
import axiosInstance from "../../../../AxiosInstance";
import usePolling from "../../../../hooks/usePolling";

// Cuánto refrescar según el rango pedido. "" es la vista en vivo (segundos);
// "month" agrega 30 días de RelayEntry del lado del servidor, así que no tiene
// sentido pedirlo seguido.
const OUTPUT_DATA_POLL_MS = {
  "": 5000,
  hour: 15000,
  day: 30000,
  week: 60000,
  month: 60000,
};

export default function OutputGraphicGroup({ selectedOutput, pdui_id }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [dataHour, setDataHour] = useState([]);
  const [graphNames, setGraphNames] = useState({});
  const [filter, setFilter] = useState("");
  const [hasSeconds, setHasSeconds] = useState(false);
  const [hasTime, setHasTime] = useState(true);
  const [loading, setLoading] = useState(false);

  const getData = async (isActive) => {
    const url = `pdui/${pdui_id}/output_data/?filter=${filter}`;

    try {
      const response = await axiosInstance.get(url, {});
      if (!isActive?.()) return;

      setData(
        response.data.map((item) => {
          item.avg_power = (item.avg_power / 1000).toFixed(1);
          item.avg_voltage = (item.avg_voltage / 1000).toFixed(1);
          item.avg_current = (item.avg_current / 1000).toFixed(1);
          return item;
        })
      );
    } catch (error) {
      console.error("Error fetching output data:", error);
    }
  };

  const getDataHour = (isActive) =>
    axiosInstance
      .get(`pdui/${pdui_id}/output_data_hour/?number=${selectedOutput}`)
      .then((response) => {
        if (isActive && !isActive()) return;
        if (response?.status !== 200) {
          return;
        }
        setDataHour(response.data);
      })
      .catch((error) => {
        console.error("Error fetching hourly output data:", error);
      });

  // El coste de output_data crece con el rango: "month" trae 30 días de
  // RelayEntry. Se consulta con la frecuencia que cada rango justifica en vez
  // de cada segundo para todos.
  usePolling(getData, OUTPUT_DATA_POLL_MS[filter] ?? 10000, true, filter);

  // output_data_hour calcula promedios por hora y además escribe 24 filas, así
  // que se pide una vez por hora. Antes iba dentro del tick de 1 s con un guard
  // de "minuto 0, segundo 0", que dependía de que el tick cayera justo.
  usePolling(getDataHour, 3600000, true, selectedOutput);

  useEffect(() => {
    if (selectedOutput === -1) {
      setGraphNames({
        voltage: "input_voltage",
        current: "input_current",
        power: "input_power",
      });
    } else {
      setGraphNames({
        voltage: `voltages[${selectedOutput - 1}]`,
        current: `currents[${selectedOutput - 1}]`,
        power: `powers[${selectedOutput - 1}]`,
      });
    }
  }, [selectedOutput]);

  // Se deriva del filtro, no hace falta recalcularlo en cada tick.
  useEffect(() => {
    setHasSeconds(filter === "");
    setHasTime(filter === "" || filter === "hour" || filter === "day");
  }, [filter]);

  // const anio = new Date().getFullYear();
  //
  // const mes = () => {
  //   const nombreMes = new Intl.DateTimeFormat("es-ES", {
  //     month: "long",
  //   }).format(new Date());
  //   return nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1);
  // };

  const onFilterClick = async (_filter) => {
    // setLoading(true);
    setFilter(_filter);
    // try {
    //   const response = await getResponse();
    //   setData(response);
    // } catch (error) {
    //   console.error(error);
    // } finally {
    //   setLoading(false);
    // }
  };

  const filters = () => (
    <div className={"d-flex"}>
      <div
        className={`${styles.frequency} ${filter === "" ? styles.frequencySelected : ""}`}
        onClick={() => onFilterClick("")}
        style={loading && filter === "" ? { pointerEvents: "none" } : {}}
      >
        {t('graphs.last60')}
        {filter === "" && loading && <div className={styles.spinner}></div>}
      </div>
      <div
        className={`${styles.frequency} ${filter === "hour" ? styles.frequencySelected : ""}`}
        onClick={() => onFilterClick("hour")}
        style={loading && filter === "hour" ? { pointerEvents: "none" } : {}}
      >
        {t('graphs.hour')}
        {filter === "hour" && loading && <div className={styles.spinner}></div>}
      </div>
      <div
        className={`${styles.frequency} ${filter === "day" ? styles.frequencySelected : ""}`}
        onClick={() => onFilterClick("day")}
        style={loading && filter === "day" ? { pointerEvents: "none" } : {}}
      >
        {t('graphs.day')}
        {filter === "day" && loading && <div className={styles.spinner}></div>}
      </div>
      <div
        className={`${styles.frequency} ${filter === "week" ? styles.frequencySelected : ""}`}
        onClick={() => onFilterClick("week")}
        style={loading && filter === "week" ? { pointerEvents: "none" } : {}}
      >
        {t('graphs.week')}
        {filter === "week" && loading && <div className={styles.spinner}></div>}
      </div>
      {/* <div */}
      {/*   className={`${styles.frequency} ${filter === "month" ? styles.frequencySelected : ""}`} */}
      {/*   onClick={() => onFilterClick("month")} */}
      {/*   style={loading && filter === "month" ? { pointerEvents: "none" } : {}} */}
      {/* > */}
      {/*   Mes */}
      {/*   {filter === "month" && loading && ( */}
      {/*     <div className={styles.spinner}></div> */}
      {/*   )} */}
      {/* </div> */}
    </div>
  );

  return (
    <div
      style={{
        backgroundColor: "#F8F8F8",
        borderRadius: "11px",
        padding: "9px",
        boxShadow:
          "rgba(0, 0, 0, 8%) 0 8px 13px -2px, rgba(27, 41, 74, 0.29) 0 17px 4px -14px",
      }}
    >
      {/* GENERAL */}
      <LineGraph
        unit={"Wh"}
        min={0}
        max={100}
        color={"#8B41C5"}
        secondaryColor={"#8B41C5"}
        entries={dataHour}
        valueName={"wh"}
        keyProperty="hour_start"
        header={[
          <div
            key="consumo-header"
            className={
              "d-flex justify-content-between align-items-center px-3 py-3"
            }
          >
            <div className={"d-flex align-items-center"}>
              <img
                alt={"thunder"}
                src={"/icons/graphs/general.png"}
                width={"14px"}
                height={"18px"}
                className={"me-2"}
              />
              <div
                className={"d-flex align-items-center"}
                style={{
                  color: "#1B365D99",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <div className={"pe-2"}>{t('graphs.electricConsumption')}</div>
                {selectedOutput !== -1 ? (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    OUT {selectedOutput}
                  </div>
                ) : (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    IN
                  </div>
                )}
              </div>
            </div>
            {/* <div className={"d-flex"}> */}
            {/*   <div className={styles.frequency}> */}
            {/*     Año <div>{anio}</div> */}
            {/*   </div> */}
            {/*   <div className={styles.frequency}> */}
            {/*     Mes <div>{mes()}</div> */}
            {/*   </div> */}
            {/*   <div className={styles.frequency}>Semana</div> */}
            {/* </div> */}
          </div>,
        ]}
      />
      <div className={"pb-3"} />
      {/* VOLTAJE */}
      <LineGraph
        unit={"V"}
        min={0}
        max={24}
        entries={data}
        color={"#E71B29"}
        valueName={graphNames?.voltage}
        keyProperty="created_at"
        secondaryColor={"#E71B29"}
        withSeconds={hasSeconds}
        withTime={hasTime}
        header={[
          <div
            key="voltaje-header"
            className={
              "d-flex justify-content-between align-items-center px-3 py-3"
            }
          >
            <div className={"d-flex align-items-center"}>
              <img
                alt={"thunder"}
                src={"/icons/graphs/voltage.png"}
                width={"12px"}
                height={"100%"}
                className={"me-2"}
              />
              <div
                className={"d-flex align-items-center"}
                style={{
                  color: "#1B365D99",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <div className={"pe-2"}>{t('graphs.voltage')}</div>
                {selectedOutput !== -1 ? (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    OUT {selectedOutput}
                  </div>
                ) : (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    IN
                  </div>
                )}
              </div>
            </div>
            {filters()}
          </div>,
        ]}
      />
      <div className={"pb-3"} />
      {/* CORRIENTE */}
      <LineGraph
        unit={"A"}
        min={0}
        max={20}
        entries={data}
        color={"#D08E2A"}
        valueName={graphNames?.current}
        keyProperty="created_at"
        secondaryColor={"#D08E2A"}
        withSeconds={hasSeconds}
        withTime={hasTime}
        header={[
          <div
            key="corriente-header"
            className={
              "d-flex justify-content-between align-items-center px-3 py-3"
            }
          >
            <div className={"d-flex align-items-center"}>
              <img
                alt={"thunder"}
                src={"/icons/graphs/current.png"}
                width={"11px"}
                height={"17px"}
                className={"me-2"}
              />
              <div
                className={"d-flex align-items-center"}
                style={{
                  color: "#1B365D99",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <div className={"pe-2"}>{t('graphs.current')}</div>
                {selectedOutput !== -1 ? (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    OUT {selectedOutput}
                  </div>
                ) : (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    IN
                  </div>
                )}
              </div>
            </div>
            {filters()}
          </div>,
        ]}
      />
      <div className={"pb-3"} />
      {/* CONSUMO */}
      <LineGraph
        unit={"W"}
        min={0}
        max={480}
        entries={data}
        color={"#375395"}
        valueName={graphNames?.power}
        keyProperty="created_at"
        secondaryColor={"#375395"}
        withSeconds={hasSeconds}
        withTime={hasTime}
        header={[
          <div
            key="potencia-header"
            className={
              "d-flex justify-content-between align-items-center px-3 py-3"
            }
          >
            <div className={"d-flex align-items-center"}>
              <img
                alt={"thunder"}
                src={"/icons/graphs/power.png"}
                width={"10px"}
                height={"100%"}
                className={"me-2"}
              />
              <div
                className={"d-flex align-items-center"}
                style={{
                  color: "#1B365D99",
                  fontSize: "12px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <div className={"pe-2"}>{t('graphs.power')}</div>
                {selectedOutput !== -1 ? (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    OUT {selectedOutput}
                  </div>
                ) : (
                  <div
                    style={{
                      color: "#ED8B00",
                    }}
                  >
                    IN
                  </div>
                )}
              </div>
            </div>
            {filters()}
          </div>,
        ]}
      />
    </div>
  );
}
